import { gql } from '@apollo/client';

import type { RegistrationProperty } from 'src/utils/registrationProperty';

/**
 * Both account-creating mutations carry the page context of the FUB Registration
 * event. Declared next to the documents below so the two stay in step — the
 * backend reads these off the request body, so a missing field is silent.
 */
type RegistrationPageContext = {
  pageUrl?: string;
  pageTitle?: string;
  property?: RegistrationProperty;
};

export type RegisterVariables = {
  input: RegistrationPageContext & {
    username: string;
    email: string;
    password: string;
    fullName: string;
    registrationSource: string;
    ppcUser: boolean;
    isInitiallySubscribedToNewsletter: boolean;
    phoneNumber?: string;
    notificationPhone?: string;
    browserUUID?: string;
    smsConsent?: boolean;
  };
};

export type GoogleSignInVariables = RegistrationPageContext & {
  code: string;
  registrationSource?: string;
  ppcUser?: boolean;
  browserUUID?: string;
  isInitiallySubscribedToNewsletter?: boolean;
  smsConsent?: boolean;
};

export const AUTH_LOGIN = gql`
  mutation ($input: UsersPermissionsLoginInput!) {
    login(input: $input) {
      jwt
      user {
        id
        username
        email
        confirmed
        blocked
        isAdmin
        fullName
        assignedLead {
          id
        }
        role {
          id
          name
          description
          type
        }
      }
    }
  }
`;

export const VERIFY_EMAIL = gql`
  query ($email: String!) {
    verifyEmail(email: $email) {
      ok
      isLostLegacyPwd
      provider
      mustSetPassword
    }
  }
`;

export const GOOGLE_SIGN_IN = gql`
  mutation (
    $code: String!
    $registrationSource: String
    $ppcUser: Boolean
    $browserUUID: String
    $isInitiallySubscribedToNewsletter: Boolean
    $smsConsent: Boolean
    $pageUrl: String
    $pageTitle: String
    $property: JSON
  ) {
    googleSignIn(
      code: $code
      registrationSource: $registrationSource
      ppcUser: $ppcUser
      browserUUID: $browserUUID
      isInitiallySubscribedToNewsletter: $isInitiallySubscribedToNewsletter
      smsConsent: $smsConsent
      pageUrl: $pageUrl
      pageTitle: $pageTitle
      property: $property
    ) {
      jwt
      isNewUser
      user {
        id
        username
        email
        confirmed
        blocked
        isAdmin
        fullName
        assignedLead {
          id
        }
        role {
          id
          name
          description
          type
        }
      }
    }
  }
`;

export const VERIFY_RECAPTCHA = gql`
  query ($token: String!) {
    verifyRecaptcha(token: $token) {
      ok
      score
    }
  }
`;

export const REGISTER = gql`
  mutation ($input: UsersPermissionsRegisterInput!) {
    register(input: $input) {
      jwt
      user {
        id
        username
      }
    }
  }
`;

export const FORGOT_PASSWORD = gql`
  mutation ($email: String!) {
    forgotPassword(email: $email) {
      ok
    }
  }
`;

// Twin of FORGOT_PASSWORD, for accounts flagged `mustSetPassword` — created on
// the user's behalf by a contact-us enquiry, a Google sign-up or an admin, and
// never given a password. Same single-use token and same `?email=&rpToken=`
// landing link; only the email copy differs, so we don't tell someone to "reset"
// a password they never chose.
export const SET_UP_PASSWORD = gql`
  mutation ($email: String!) {
    setUpPassword(email: $email) {
      ok
    }
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword(
    $password: String!
    $passwordConfirmation: String!
    $code: String!
  ) {
    resetPassword(
      password: $password
      passwordConfirmation: $passwordConfirmation
      code: $code
    ) {
      jwt
      user {
        id
        username
        email
        confirmed
        blocked
        isAdmin
        fullName
        assignedLead {
          id
        }
      }
    }
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword(
    $currentPassword: String!
    $password: String!
    $passwordConfirmation: String!
  ) {
    changePassword(
      currentPassword: $currentPassword
      password: $password
      passwordConfirmation: $passwordConfirmation
    ) {
      jwt
    }
  }
`;

export const SEND_EMAIL_CONFIRMATION = gql`
  mutation ($email: String!) {
    sendEmailConfirmation(email: $email) {
      ok
    }
  }
`;

export const WELCOME_EMAIL = gql`
  mutation ($email: String!, $name: String!) {
    sendWelcomeEmail(email: $email, name: $name)
  }
`;
